import api from '../api'

export type ClockKind = 'in' | 'out' | 'pause_start' | 'pause_end'
export type ClockState = 'off' | 'working' | 'paused'

export interface ClockEvent {
  id: string
  kind: ClockKind
  occurred_at: string
  source: 'web' | 'mobile' | 'kiosk' | 'auto' | 'correction'
  location_result: 'not_checked' | 'inside' | 'outside' | 'unavailable'
  work_location: string | null
  distance_m: number | null
  note: string
  replaced: boolean
}

export interface ClockStatus {
  timezone: string
  server_now: string
  worked_seconds: number
  pause_seconds: number
  pause_policy: Pick<
    ClockPolicy,
    'pause_after_minutes' | 'pause_minutes' | 'long_pause_after_minutes' | 'long_pause_minutes'
  >
  state: ClockState
  since: string | null
  session_started_at: string | null
  allowed: ClockKind[]
  today: {
    date: string
    worked_minutes: number
    pause_minutes: number
    planned_minutes: number
    absence: string | null
    required_pause_minutes: number
    pauses: [string, string | null][]
    first_in: string | null
    events: ClockEvent[]
  }
  yesterday_auto_closed: boolean
  last_event: ClockEvent | null
  geofence: 'off' | 'flag'
  event?: ClockEvent
}

export interface ClockDay {
  date: string
  first_in: string | null
  last_out: string | null
  worked_minutes: number
  pause_minutes: number
  planned_minutes: number
  absence: string | null
  required_pause_minutes: number
  pause_short_minutes: number
  overtime_minutes: number
  open: boolean
  outside: boolean
  pending_corrections: number
  status:
    | 'planned'
    | 'absence'
    | 'off'
    | 'in_progress'
    | 'correction_pending'
    | 'auto_closed'
    | 'missing'
    | 'not_started'
    | 'pause_short'
    | 'complete'
  events: ClockEvent[]
  pauses: [string, string | null][]
}

export interface ClockConfirmation {
  status: 'open' | 'changed' | 'confirmed' | 'approved'
  confirmed_at: string | null
  approved_at: string | null
  approved_by: string | null
  worked_minutes?: number
}

export interface ClockWeek {
  fingerprint: string
  supports_review_version?: boolean
  pause_policy: ClockStatus['pause_policy']
  timezone: string
  week_start: string
  days: ClockDay[]
  totals: {
    worked_minutes: number
    planned_minutes: number
    pause_minutes: number
    overtime_minutes: number
    pending_corrections: number
  }
  confirmation: ClockConfirmation
  requires_confirmation: boolean
  any_open: boolean
  employee: { id: string; name: string }
}

export interface ClockCorrection {
  timezone: string
  id: string
  employee: string
  employee_name: string
  requested_by: string | null
  action: 'add' | 'change' | 'remove'
  kind: ClockKind | ''
  original_at: string | null
  proposed_at: string | null
  approved_at: string | null
  reason: string
  status: 'pending' | 'approved' | 'rejected'
  decided_by: string | null
  decided_at: string | null
  decision_note: string
  created_at: string
}

export interface ClockPolicy {
  timezone: string
  id: string | null
  country: string
  pause_after_minutes: number
  pause_minutes: number
  long_pause_after_minutes: number
  long_pause_minutes: number
  geofence_mode: 'off' | 'flag'
  auto_close_after_hours: number
  weekly_confirmation: boolean
}

export type ClockPeriod = Pick<ClockWeek, 'days' | 'totals' | 'timezone' | 'employee' | 'any_open' | 'pause_policy'> & {
  start: string
  end: string
  weeks: { week_start: string; confirmation: ClockConfirmation }[]
}
export const getClockPeriod = async (start: string, end: string, employee?: string): Promise<ClockPeriod> =>
  (await api.get('/time-clock/period/', { params: { start, end, employee } })).data
export interface ClockAuditEntry {
  sequence: number
  recorded_at: string
  action: string
  actor: string
  subject: string
  object_id: string
  details: Record<string, unknown>
  digest: string
  previous_hash: string
  payload_valid: boolean
}
export const getClockAudit = async (before?: number): Promise<{ rows: ClockAuditEntry[]; next: number | null }> =>
  (await api.get('/time-clock/audit/', { params: { before } })).data

export interface WorkLocation {
  id: string
  name: string
  address: string
  latitude: number
  longitude: number
  radius_m: number
  active: boolean
}

/** The viewer's IANA time zone: days and weeks are counted in it. */
export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Amsterdam'

export const getClockStatus = async (): Promise<ClockStatus> =>
  (await api.get('/time-clock/status/', { params: { tz: browserTimeZone() } })).data

export const punchClock = async (payload: {
  kind: ClockKind
  request_id: string
  latitude?: number
  longitude?: number
  accuracy?: number
}): Promise<ClockStatus> => (await api.post('/time-clock/punch/', { ...payload, tz: browserTimeZone() })).data

export const getClockWeek = async (start?: string, employee?: string): Promise<ClockWeek> =>
  (await api.get('/time-clock/week/', { params: { start, employee, tz: browserTimeZone() } })).data

export const confirmClockWeek = async (weekStart: string, fingerprint: string): Promise<ClockWeek> =>
  (await api.post('/time-clock/week/confirm/', { week_start: weekStart, fingerprint, tz: browserTimeZone() })).data

export const getClockCorrections = async (scope: 'mine' | 'team', status?: string): Promise<ClockCorrection[]> =>
  (await api.get('/time-clock/corrections/', { params: { scope, status } })).data

export interface ClockCorrectionImpact {
  timezone: string
  as_of: string
  worked_delta_minutes: number
  pause_delta_minutes: number
  open: boolean
  days: {
    date: string
    before_worked_minutes: number
    after_worked_minutes: number
    worked_delta_minutes: number
    before_pause_minutes: number
    after_pause_minutes: number
    pause_delta_minutes: number
    open: boolean
  }[]
}
export const getClockCorrectionImpact = async (id: string, adjustedAt?: string): Promise<ClockCorrectionImpact> =>
  (await api.get(`/time-clock/corrections/${id}/preview/`, { params: { adjusted_at: adjustedAt } })).data

export interface ClockCorrectionDraft {
  action: ClockCorrection['action']
  event?: string
  kind?: ClockKind
  proposed_at?: string
  employee?: string
}
export const previewClockCorrection = async (payload: ClockCorrectionDraft): Promise<ClockCorrectionImpact> =>
  (await api.post('/time-clock/corrections/preview/', payload)).data
export const requestClockCorrection = async (
  payload: ClockCorrectionDraft & { reason: string },
): Promise<ClockCorrection> => (await api.post('/time-clock/corrections/', { ...payload, tz: browserTimeZone() })).data

export const decideClockCorrection = async (
  id: string,
  approve: boolean,
  note = '',
  adjustedAt?: string,
): Promise<ClockCorrection> =>
  (
    await api.post(`/time-clock/corrections/${id}/decide/`, {
      approve,
      note,
      adjusted_at: adjustedAt,
      tz: browserTimeZone(),
    })
  ).data

export const getTeamToday = async () =>
  (await api.get('/time-clock/team/today/', { params: { tz: browserTimeZone() } })).data
export interface TeamClockDay {
  employee: string
  name: string
  state: ClockState
  since: string | null
  first_in: string | null
  worked_minutes: number
  pause_minutes: number
  planned_minutes: number
  outside: boolean
  country: string
  teams: { id: string; name: string }[]
}
export type TeamClockWeek = ClockWeek['totals'] & {
  employee: string
  name: string
  any_open: boolean
  confirmation: ClockConfirmation
  requires_confirmation: boolean
  issues: number
  outside: boolean
  days: Pick<
    ClockDay,
    'date' | 'worked_minutes' | 'pause_minutes' | 'planned_minutes' | 'pause_short_minutes' | 'status'
  >[]
  country: string
  teams: { id: string; name: string }[]
}
export const getTypedTeamToday = async (): Promise<TeamClockDay[]> => getTeamToday()
export const getTeamWeek = async (start?: string): Promise<{ week_start: string; rows: TeamClockWeek[] }> =>
  (await api.get('/time-clock/team/week/', { params: { start, tz: browserTimeZone() } })).data
export type TeamClockPeriod = Omit<TeamClockWeek, 'confirmation' | 'requires_confirmation'> & {
  timezone: string
  weeks: ClockPeriod['weeks']
}
export const getTeamPeriod = async (
  start: string,
  end: string,
): Promise<{ start: string; end: string; rows: TeamClockPeriod[] }> =>
  (await api.get('/time-clock/team/period/', { params: { start, end } })).data
export const approveTeamWeek = async (employee: string, weekStart: string, fingerprint: string): Promise<ClockWeek> =>
  (
    await api.post('/time-clock/team/week/approve/', {
      employee,
      week_start: weekStart,
      fingerprint,
      tz: browserTimeZone(),
    })
  ).data

export const getClockPolicies = async (): Promise<ClockPolicy[]> => (await api.get('/time-clock/policy/')).data
export const saveClockPolicy = async (policy: Partial<ClockPolicy>): Promise<ClockPolicy> =>
  (await api.put('/time-clock/policy/', policy)).data
export const getWorkLocations = async (): Promise<WorkLocation[]> => (await api.get('/time-clock/locations/')).data
export const createWorkLocation = async (
  location: Omit<WorkLocation, 'id' | 'active'> & { active?: boolean },
): Promise<WorkLocation> => (await api.post('/time-clock/locations/', location)).data
export const updateWorkLocation = async (id: string, values: Partial<WorkLocation>): Promise<WorkLocation> =>
  (await api.patch(`/time-clock/locations/${id}/`, values)).data
export const deleteWorkLocation = async (id: string) => {
  await api.delete(`/time-clock/locations/${id}/`)
}
export const clockExportUrl = (start: string, end: string) =>
  `/time-clock/export/?start=${start}&end=${end}&tz=${encodeURIComponent(browserTimeZone())}`
export const downloadClockExport = async (
  start: string,
  end: string,
  options?: {
    employee?: string
    scope?: 'mine' | 'team'
    format?: 'csv' | 'xlsx'
    approved_only?: boolean
    search?: string
    country?: string
    team?: string
  },
) => {
  const response = await api.get(clockExportUrl(start, end), {
    responseType: 'blob',
    params: { ...options, format: undefined, file_type: options?.format },
  })
  const url = URL.createObjectURL(response.data)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `hours-${start}-${end}.${options?.format || 'csv'}`
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
